import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, AfterViewInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';

interface DebtInfo {
    cardName: string;
    cardNumber: string;
    amount: number;
    dueDate: string;
    usedPercentage: number;
    limit: number;
}

@Component({
    selector: 'app-debts-summary-widget',
    standalone: true,
    imports: [CommonModule, WidgetEdit, CdkOverlayOrigin],
    templateUrl: './debts-summary-widget.html',
    styleUrl: './debts-summary-widget.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DebtsSummaryWidgetComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('progressCanvas') progressCanvasRef!: ElementRef<HTMLCanvasElement>;

    @Input() isEditMode: boolean = false;
    @Input() isAddMode: boolean = false;
    @Output() addWidget = new EventEmitter<any>();
    @Input() size: 'small' | 'medium' | 'half' | 'full' = 'small';
    @Output() editCall = new EventEmitter<any>();
    @Input() index = 0;
    @Input() activeDropdownIndex: number | null = null;
    @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
    @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
    @Input() changeWidgetSize!: (index: number, size: any) => void;
    @Input() removeWidget!: (index: number) => void;
    @Input() title = 'Debts Summary';

    // Data inputs
    @Input() debtInfo: DebtInfo = {
        cardName: 'Credit Card 1',
        cardNumber: '**** 1234',
        amount: 50000,
        dueDate: 'Ends 15 Jun',
        usedPercentage: 90,
        limit: 100000
    };

    private resizeObserver: ResizeObserver | null = null;

    constructor(
        private elementRef: ElementRef,
        private cdr: ChangeDetectorRef
    ) { }

    ngAfterViewInit() {
        setTimeout(() => {
            this.drawProgressBar();
            this.updateLabelFontSizes();
            this.setupResizeObserver();
        }, 0);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['debtInfo'] && !changes['debtInfo'].firstChange) {
            setTimeout(() => {
                this.drawProgressBar();
            }, 0);
        }
    }

    private setupResizeObserver() {
        // Find the parent article.widget element
        const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;

        if (!widgetElement) {
            if (!this.isAddMode) {
                console.warn('Could not find parent widget element');
            }
            return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
            window.requestAnimationFrame(() => {
                if (!this.resizeObserver) return;
                const isDragging = document.body.classList.contains('is-resizing');
                const resizingWidgetType = document.body.dataset['resizingWidget'];

                // Only update if THIS widget type is being resized
                if (isDragging && resizingWidgetType === 'debts') {
                    // Redraw canvas with new dimensions
                    this.drawProgressBar();
                    // Update label font sizes
                    this.updateLabelFontSizes();
                    this.cdr.detectChanges();
                }
            });
        });
        this.resizeObserver.observe(widgetElement);
    }

    private updateLabelFontSizes() {
        const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;
        if (!widgetElement) return;

        const widgetHeight = widgetElement.offsetHeight;

        // Linear interpolation: 12px at 226px height → 14px at 288px height
        const minFontSize = 12;
        const maxFontSize = 16;
        const minWidgetHeight = 226;
        const maxWidgetHeight = 288;

        let fontSize: number;
        if (widgetHeight <= minWidgetHeight) {
            fontSize = minFontSize;
        } else if (widgetHeight >= maxWidgetHeight) {
            fontSize = maxFontSize;
        } else {
            const progress = (widgetHeight - minWidgetHeight) / (maxWidgetHeight - minWidgetHeight);
            fontSize = minFontSize + (progress * (maxFontSize - minFontSize));
        }

        // Apply font size to all label elements
        const labelElements = this.elementRef.nativeElement.querySelectorAll(
            '.debts-widget__label-text, .debts-widget__limit-text'
        );

        labelElements.forEach((element: HTMLElement) => {
            element.style.fontSize = `${fontSize}px`;
        });
    }

    private drawProgressBar() {
        if (!this.progressCanvasRef) return;

        const canvas = this.progressCanvasRef.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Get the widget element to calculate dynamic height
        const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;
        const widgetHeight = widgetElement ? widgetElement.offsetHeight : 226;

        // Calculate dynamic canvas height based on widget height
        // Linear interpolation: 4px at 226px height -> 12px at 288px height
        const minHeight = 4;
        const maxHeight = 16;
        const minWidgetHeight = 226;
        const maxWidgetHeight = 260;

        let dynamicHeight: number;
        if (widgetHeight <= minWidgetHeight) {
            dynamicHeight = minHeight;
        } else if (widgetHeight >= maxWidgetHeight) {
            dynamicHeight = maxHeight;
        } else {
            // Linear interpolation between min and max
            const progress = (widgetHeight - minWidgetHeight) / (maxWidgetHeight - minWidgetHeight);
            dynamicHeight = minHeight + (progress * (maxHeight - minHeight));
        }

        // Set the container height to match the canvas height
        const progressBarContainer = canvas.parentElement as HTMLElement;
        if (progressBarContainer) {
            progressBarContainer.style.height = `${dynamicHeight}px`;
        }

        // Set canvas size with device pixel ratio for sharp rendering
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = dynamicHeight * dpr;
        ctx.scale(dpr, dpr);

        // Clear canvas
        ctx.clearRect(0, 0, rect.width, dynamicHeight);

        const barHeight = dynamicHeight;
        const borderRadius = Math.min(barHeight / 2, 4);

        // Get computed colors from CSS variables
        const bgColor = this.getCSSVariable('--bg-primary') || '#EBEDEF';
        const orangeColor = this.getCSSVariable('--color-orange') || '#E7733A';

        // Draw background bar
        ctx.fillStyle = bgColor;
        this.roundRect(ctx, 0, 0, rect.width, barHeight, borderRadius);

        // Draw progress bar with glow effect
        const progressWidth = (this.debtInfo.usedPercentage / 100) * rect.width;

        // Draw glow/shadow
        ctx.shadowColor = orangeColor;
        ctx.shadowBlur = 6;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 3;

        ctx.fillStyle = orangeColor;
        this.roundRect(ctx, 0, 0, progressWidth, barHeight, borderRadius);

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
    }

    private getCSSVariable(variableName: string): string {
        return getComputedStyle(document.documentElement)
            .getPropertyValue(variableName)
            .trim();
    }

    private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        ctx.fill();
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
}
